<script setup lang="ts">
import {
  computed,
  onMounted,
  onUnmounted,
  provide,
  ref,
  toRef,
  watch,
} from 'vue';
import SplitButton from 'primevue/splitbutton';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import TabPanels from 'primevue/tabpanels';
import TabPanel from 'primevue/tabpanel';
import Textarea from 'primevue/textarea';
import type { MenuItem } from 'primevue/menuitem';
import Button from 'primevue/button';
import {
  EventBusEvents,
  OpportunityFlyoutActionButtonFlags as ActionFlags,
  useActivities,
  useAssociationMiniCards,
  useCrm,
  useEventBus,
  useOpportunities,
  useOpportunityDetailFlyout,
  useResponsive,
  useStringFunctions,
} from '@apparatix/composables';
import { useCreateActivity } from '@apparatix/composables/useCreateActivity';
import { useOpportunityStore } from '@apparatix/stores/useOpportunityStore';
import type { OpportunityType, OpportunityUpdatePayload } from '@apparatix/types/Leads';
import ProjectionsOverview from './ProjectionsOverview.vue';
import FileUploader from './FileUploader.vue';
import AssociationsEditor from './AssociationsEditor.vue';
import CampaignDealsDocsPanel from '@apparatix/leads/CampaignDealsDocsPanel.vue';
import FormSkeleton from '@apparatix/Components/FormSkeleton.vue';
import OpportunityDetailEditor from './OpportunityDetailEditor.vue';
import MarketingDetailsTab from './MarketingDetailsTab.vue';
import { Section, Typography } from '@apparatix/library/core';
import { button as greenButtonTheme } from '@apparatix/theme/Green';
import ActivitiesTab from './activities/ActivitiesTab.vue';
import AddActivityButton from './activities/AddActivityButton.vue';
import { CrmDrawerDataReadyEvent, type DetailTabName, type FlyoutNavigationContext } from './types';
import { InjectEventNames } from '@apparatix/constants';
import { ActivityAddedEvent, ActivityUpdatedEvent, ActivityDeletedEvent } from './activities/commonProps';
import { Relationship } from '@apparatix/schemas/Relationship';
import { useRouter, useRoute } from 'vue-router';
import { Activity } from '@apparatix/schemas/Activities';
import MiniCardGrid from '@apparatix/leads/MiniCardGrid.vue';
import DetailDrawerHeader from './DetailDrawerHeader.vue';
import Stepper from '@apparatix/library/Stepper.vue';

interface LeadDetailFlyoutProps {
  type: OpportunityType;
  id: number;
  activeTab?: DetailTabName;
}

const props = defineProps<LeadDetailFlyoutProps>();

const activeTab = ref<DetailTabName>(props.activeTab || 'overview');

const { formatAsCurrency } = useStringFunctions();
const { handleDetailTabUpdate, isDetailTab } = useCrm();
const store = useOpportunityStore();
const helper = useOpportunities();
const responsive = useResponsive();

const {
  createProposal,
  defaultDocument,
  documents,
  hasFlag,
  isDealsAndDocsLoading,
  isProposalBlockedByProjection,
  openDocument,
  openDocumentsMenuItems,
  proposalProjectionWarning,
} = useOpportunityDetailFlyout(props.type);

const {
  activityTabSections,
  insertOrUpdateActivityInList,
  isLoading: isActivitiesLoading,
  lastTask,
  loadActivities,
  postActivity,
  removeActivityFromList,
  upcomingTask,
} = useActivities();

const { addActivityComponent, getMobileMenuItems } = useCreateActivity();

const { emitEvent } = useEventBus();

const router = useRouter();
const route = useRoute();

const onActivityAddedOrUpdated = (activity: Activity, shouldReload: boolean) => {
  if (shouldReload) {
    loadActivities(props.id, 'accounts');
  } else {
    insertOrUpdateActivityInList(activity);
  }
};

provide<ActivityAddedEvent>(InjectEventNames.crm.activities.activityCreated, onActivityAddedOrUpdated);
provide<ActivityUpdatedEvent>(InjectEventNames.crm.activities.activityUpdated, onActivityAddedOrUpdated);
provide<ActivityDeletedEvent>(InjectEventNames.crm.activities.activityDeleted, removeActivityFromList);

const activityParentObject = computed<Relationship>(() => ({ id: props.id, name: store.selectedOpportunity[props.type]?.opportunity?.name, type: props.type }));

onUnmounted(() => {
  store.selectedOpportunity[props.type] = undefined;
  helper.closeDetailFlyout(props.type);
});

onMounted(() => loadActivities(activityParentObject.value.id, props.type === 'Lead' ? 'leads' : 'opportunities'));

const {
  account: miniCardAccount,
  accountsLoading: accountMiniCardLoading,
  contact: miniCardContact,
  contactsLoading: contactMiniCardLoading,
  shouldShowAccount: showAccountMiniCard,
  shouldShowContact: showContactMiniCard,
} = useAssociationMiniCards(toRef(store, 'campaignAccounts'), toRef(store, 'campaignContacts'));

const hasUnsavedNoteChanges = ref(false);
const isSavingNotes = ref(false);
const projectionsChartLoaded = ref(false);

watch(
  () => props.id,
  async (newCampaignId) => {
    if (newCampaignId) {
      await helper.openDetailFlyout(props.type, props.id);
      emitEvent<CrmDrawerDataReadyEvent>(EventBusEvents.CrmDetailLoaded, { title: store.selectedOpportunity[props.type].opportunity.name, type: props.type });
    }
  },
  { immediate: true },
);

watch(() => store.selectedOpportunity[props.type]?.opportunity?.notes, (newNotes, oldNotes) => {
  if (!newNotes || !oldNotes) return;
  if (newNotes !== oldNotes) {
    hasUnsavedNoteChanges.value = true;
  }
});

watch(() => store.selectedOpportunity[props.type]?.planData?.campaignObjective, (newObjective, oldObjective) => {
  if (!newObjective || !oldObjective) return;
  if (newObjective !== oldObjective) {
    hasUnsavedNoteChanges.value = true;
  }
});

watch(() => store.selectedOpportunity[props.type]?.planData?.miscNotes, (newMisc, oldMisc) => {
  if (!newMisc || !oldMisc) return;
  if (newMisc !== oldMisc) {
    hasUnsavedNoteChanges.value = true;
  }
});

watch(() => store.selectedOpportunity[props.type]?.planData?.solutionsAndStrategy, (newSolutions, oldSolutions) => {
  if (!newSolutions || !oldSolutions) return;
  if (newSolutions !== oldSolutions) {
    hasUnsavedNoteChanges.value = true;
  }
});

watch(() => store.selectedOpportunity[props.type]?.planData?.statedNeeds, (newNeeds, oldNeeds) => {
  if (!newNeeds || !oldNeeds) return;
  if (newNeeds !== oldNeeds) {
    hasUnsavedNoteChanges.value = true;
  }
});

watch(() => route.path, newPath => {
  if (!newPath) return;

  const segments = newPath.split('/');
  const tab = segments.pop();

  if (isDetailTab(tab)) {
    activeTab.value = <DetailTabName>tab;
  }
});

const steps = computed(() => {
  const swimlanes = store.selectedOpportunity[props.type]?.swimlanes ?? [];
  const currentLane = swimlanes.find(lane => lane.id === store.selectedOpportunity[props.type]?.opportunity?.swimlaneId);

  let laneIndex = swimlanes.length - 1;
  if (currentLane?.id) {
    laneIndex = swimlanes.findIndex(lane => lane.id === currentLane.id);
  }

  if (responsive.isScreenAtOrAbove('lg')) {
    return {
      lanes: swimlanes,
      currentStep: laneIndex,
    };
  }

  // Safety for if we only have one lane
  if (swimlanes.length === 1) {
    return {
      lanes: swimlanes,
      currentStep: 0,
    };
  }

  let leftIndex: number;
  let rightIndex: number;
  let currentStep: number;

  if (laneIndex === 0) {
    leftIndex = 0;
    rightIndex = 1;
    currentStep = 0;
  } else if (laneIndex >= swimlanes.length - 1) {
    leftIndex = swimlanes.length - 2;
    rightIndex = swimlanes.length - 1;
    currentStep = 1;
  } else {
    leftIndex = laneIndex;
    rightIndex = laneIndex + 1;
    currentStep = 0;
  }

  return {
    lanes: [swimlanes[leftIndex], swimlanes[rightIndex]],
    currentStep: currentStep,
  };
});


const mobileMenuItems = computed<MenuItem[]>(() => {
  const activityParent: Relationship = {
    name: store.selectedOpportunity[props.type]?.opportunity?.name,
    id: store.selectedOpportunity[props.type]?.opportunity?.id,
    type: props.type,
  };

  const baseItems: MenuItem[] = getMobileMenuItems(['Note', 'Reminder', 'Task'], props.type === 'Lead' ? 'leads' : 'opportunities', activityParent);
  const conditionalItems: MenuItem[] = [{ separator: true }];

  if (hasFlag(ActionFlags.DuplicateOpportunity)) {
    conditionalItems.push({
      label: 'Duplicate opportunity',
      icon: 'pi pi-pencil',
      command: () => createRenewalOpportunityClick(),
    });
  }

  if (hasFlag(ActionFlags.CreateProposal) && !hasFlag(ActionFlags.OpenProposalDropdown)) {
    conditionalItems.push({
      label: 'Create proposal',
      icon: 'pi pi-pencil',
      blocked: isProposalBlockedByProjection.value,
      tooltip: proposalProjectionWarning.value || undefined,
      command: () => createProposal(),
    });
  }

  if (hasFlag(ActionFlags.OpenProposal) && !hasFlag(ActionFlags.OpenProposalDropdown)) {
    conditionalItems.push({
      label: 'Open proposal',
      icon: 'pi pi-pencil',
      command: () => openDocument(defaultDocument.value),
    });
  }

  if (hasFlag(ActionFlags.ConvertLead)) {
    conditionalItems.push({
      label: 'Convert lead',
      icon: 'pi pi-sync',
      command: () => openLeadConversionModal(),
    });
  }

  if (hasFlag(ActionFlags.OpenProposalDropdown)) {
    conditionalItems.push({
      label: 'Documents',
      items: openDocumentsMenuItems.value,
    });
  }

  return [...baseItems, ...conditionalItems];
});

const createRenewalOpportunityClick = async () => {
  const newOpportunityId = await helper.duplicateCampaign(props.id);
  if (newOpportunityId) {
    store.isDetailLoading[props.type] = true;
    handleNavigateForward({ view: props.type, id: newOpportunityId });
  }
};

const openLeadConversionModal = () => {
  store.showOpportunityCreationModal = true;
  store.createOpportunityContext = 'convert-lead';
};


// Arguably crude management to check if the graph needs to re-fetch data
const graphReload = ref(0);

const handleContentUpdate = () => {
  graphReload.value += 1;
};

const onSaveNoteChanges = async () => {
  isSavingNotes.value = true;
  const planData = store.selectedOpportunity[props.type].planData;

  const payload: OpportunityUpdatePayload = {
    data: {
      opportunity: [
        {
          id: props.id,
          notes: store.selectedOpportunity[props.type].opportunity.notes,
          planData: planData,
        },
      ],
    },
  };

  await helper.updateOpportunity(payload, false);
  isSavingNotes.value = false;
  hasUnsavedNoteChanges.value = false;
};

const handleNavigateForward = (context: FlyoutNavigationContext) => {
  const { basePath } = route.meta;
  const path = `${basePath}/detail/${context.view.toLowerCase()}/${context.id}`;

  router.push({ path });
};

const postActivityForward = (payload) => {
  postActivity(payload.typeId, payload.modelObject.id, payload.modelObject.name, payload.modelObject.view);
};

</script>
<template>
  <DetailDrawerHeader
    v-if="!store.isDetailLoading[props.type]"
    :type="props.type"
    :menu-items="mobileMenuItems"
    :title="store.selectedOpportunity[props.type].opportunity.name"
    :subtitle="`Projected value ${formatAsCurrency(store.selectedOpportunity[props.type]?.opportunity?.value, false)}`"
  >
    <template #buttons>
      <AddActivityButton
        :view="props.type === 'Lead' ? 'leads' : 'opportunities'"
        :parent-object="activityParentObject"
        :button-pt-props="{outlined: true, severity: 'secondary'}"
      />
      <Button
        v-if="hasFlag(ActionFlags.DuplicateOpportunity)"
        label="Duplicate Opportunity"
        size="small"
        severity="primary"
        icon="pi pi-pencil"
        @click="createRenewalOpportunityClick"
      />
      <!--
        Wrapper div is required so the tooltip still appears on hover: browsers don't emit
        hover events on disabled buttons, so the PrimeVue tooltip must live on the wrapper.
      -->
      <div
        v-if="hasFlag(ActionFlags.CreateProposal) && !hasFlag(ActionFlags.OpenProposalDropdown)"
        v-tooltip.top="proposalProjectionWarning"
      >
        <Button
          :disabled="hasFlag(ActionFlags.DisableCreateProposal) || isProposalBlockedByProjection"
          label="Create Proposal"
          size="small"
          :severity="store.canGenerateProposalOnLead[props.type] ? 'secondary' : 'primary'"
          icon="pi pi-pencil"
          @click="createProposal"
        />
      </div>
      <Button
        v-if="hasFlag(ActionFlags.OpenProposal) && !hasFlag(ActionFlags.OpenProposalDropdown)"
        label="Open Proposal"
        size="small"
        :disabled="hasFlag(ActionFlags.DisableOpenProposal)"
        @click="openDocument(defaultDocument)"
      />
      <Button
        v-if="hasFlag(ActionFlags.ConvertLead)"
        icon="pi pi-pencil"
        label="Convert Lead"
        :disabled="!store.stateReady['Opportunity'] || hasFlag(ActionFlags.DisableConvertOrDuplicate)"
        size="small"
        severity="primary"
        @click="openLeadConversionModal"
      />
      <SplitButton
        v-if="hasFlag(ActionFlags.OpenProposalDropdown)"
        size="small"
        label="Open Proposal"
        :model="openDocumentsMenuItems"
        @click="openDocument(defaultDocument)"
      />
    </template>
  </DetailDrawerHeader>
  <Stepper
    v-if="!store.isDetailLoading[props.type]"
    label-field="status"
    :steps="steps.lanes"
    :current-step="steps.currentStep"
  />
  <Tabs
    v-if="!store.isDetailLoading[props.type]"
    class="grow"
    :value="activeTab"
    @update:value="(val: DetailTabName) => handleDetailTabUpdate(val, () => activeTab = val)"
  >
    <TabList class="overflow-x-auto whitespace-nowrap scrolling-touch">
      <Tab value="overview" class="flex gap-2 items-center">
        <i class="pi pi-globe" />
        Overview
      </Tab>
      <Tab value="relationships" class="flex gap-2 items-center">
        <i class="pi pi-sitemap" />
        Relationships
      </Tab>
      <Tab value="deals" class="flex gap-2 items-center">
        <span class="pi" :class="[isDealsAndDocsLoading ? 'pi-spin pi-spinner' : 'pi-briefcase']" />
        Deals &amp; Docs
      </Tab>
      <Tab value="activity" class="flex gap-2 items-center">
        <i class="pi pi-calendar" />
        Activity
      </Tab>
      <Tab value="files" class="flex gap-2 items-center">
        <i class="pi pi-folder" />
        Files
      </Tab>
    </TabList>
    <TabPanels class="!bg-[var(--neutral-background)] grow">
      <div class="w-full grid grid-cols-3 grid-flow-row auto-rows-auto lg:gap-x-9 gap-y-4 p-2 lg:p-9">
        <div class="flex flex-col gap-4 col-span-3">
          <TabPanel value="overview">
            <div class="flex flex-col gap-6">
              <MiniCardGrid
                :show-account-mini-card="showAccountMiniCard"
                :show-contact-mini-card="showContactMiniCard"
                :mini-card-account="miniCardAccount"
                :mini-card-contact="miniCardContact"
                :account-mini-card-loading="accountMiniCardLoading"
                :contact-mini-card-loading="contactMiniCardLoading"
                :last-task="lastTask"
                :upcoming-task="upcomingTask"
                :is-loading="isActivitiesLoading"
                @post-activity="postActivityForward"
              />
              <FormSkeleton v-if="!store.selectedOpportunity[props.type] || !store.stateReady[props.type]" />
              <OpportunityDetailEditor
                v-if="store.selectedOpportunity[props.type] && store.stateReady[props.type]"
                :type="props.type"
                @handle-content-update="handleContentUpdate"
              />
              <MarketingDetailsTab
                v-if="props.type === 'Opportunity' && store.selectedOpportunity[props.type]"
                :type="props.type"
                :opportunity-id="store.selectedOpportunity[props.type].opportunity.id"
                :revenue-data="store.selectedOpportunity[props.type].revenueData"
                :can-view-audience="store.canViewAudience[props.type]"
              />
              <template v-if="props.type === 'Opportunity'">
                <Section class="mb-3" :footer="false">
                  <Typography variant="section-heading">
                    Revenue Projections
                  </Typography>
                  <ProjectionsOverview
                    v-if="store.selectedOpportunity[props.type]"
                    :opportunity="store.selectedOpportunity[props.type]"
                    :graph-reload="graphReload"
                    @on:chart-loaded="projectionsChartLoaded = true"
                  />
                </Section>
              </template>

              <Section v-if="store.selectedOpportunity[props.type]" class="mb-3" :footer="false">
                <div class="flex flex-col gap-2">
                  <Typography variant="section-heading">
                    Notes
                  </Typography>
                  <Textarea
                    v-model="store.selectedOpportunity[props.type].opportunity.notes"
                    rows="4"
                    fluid
                    auto-resize
                    :disabled="isSavingNotes"
                  />
                </div>

                <template v-if="props.type === 'Opportunity'">
                  <div class="flex flex-col gap-2">
                    <Typography variant="section-heading">
                      Objectives
                    </Typography>
                    <Textarea
                      v-model="store.selectedOpportunity[props.type].planData.campaignObjective"
                      rows="4"
                      fluid
                      auto-resize
                      :disabled="isSavingNotes"
                    />
                  </div>

                  <div class="flex flex-col gap-2">
                    <Typography variant="section-heading">
                      Stated &amp; Uncovered Needs
                    </Typography>
                    <Textarea
                      v-model="store.selectedOpportunity[props.type].planData.statedNeeds"
                      rows="4"
                      fluid
                      auto-resize
                      :disabled="isSavingNotes"
                    />
                  </div>

                  <div class="flex flex-col gap-2">
                    <Typography variant="section-heading">
                      Solutions &amp; Strategies
                    </Typography>
                    <Textarea
                      v-model="store.selectedOpportunity[props.type].planData.solutionsAndStrategy"
                      rows="4"
                      fluid
                      auto-resize
                      :disabled="isSavingNotes"
                    />
                  </div>

                  <div class="flex flex-col gap-2">
                    <Typography variant="section-heading">
                      Misc. Notes
                    </Typography>
                    <Textarea
                      v-model="store.selectedOpportunity[props.type].planData.miscNotes"
                      rows="4"
                      fluid
                      auto-resize
                      :disabled="isSavingNotes"
                    />
                  </div>
                </template>
                <div class="flex items-center pt-4">
                  <Button
                    label="Save"
                    :severity="hasUnsavedNoteChanges ? 'primary' : 'secondary'"
                    :dt="greenButtonTheme"
                    size="small"
                    :disabled="!hasUnsavedNoteChanges || isSavingNotes"
                    @click="onSaveNoteChanges"
                  />
                </div>
              </Section>
            </div>
          </TabPanel>
          <TabPanel value="relationships">
            <AssociationsEditor
              context="editing"
              tab-id="overview"
              :type="props.type"
              :opportunity-id="props.id"
              @navigate-forward="handleNavigateForward"
              @on:campaign-accounts-loaded="accountMiniCardLoading = false"
              @on:campaign-contacts-loaded="contactMiniCardLoading = false"
              @post-activity="postActivityForward"
            />
          </TabPanel>
          <TabPanel value="deals">
            <div class="flex flex-col">
              <template v-if="true">
                <CampaignDealsDocsPanel
                  :documents="documents"
                  tab-id="overview"
                  :is-loading="isDealsAndDocsLoading"
                  :type="props.type"
                  @navigate-forward="handleNavigateForward"
                />
              </template>
            </div>
          </TabPanel>
          <TabPanel value="activity">
            <ActivitiesTab
              :view="props.type === 'Lead' ? 'leads' : 'opportunities'"
              :parent-object="activityParentObject"
              :sections="activityTabSections"
              :is-loading="isActivitiesLoading"
            />
          </TabPanel>
          <TabPanel value="files">
            <div class="flex flex-col">
              <template v-if="true">
                <FileUploader :id="props.id" endpoint="opportunities" />
              </template>
            </div>
          </TabPanel>
        </div>
      </div>
    </TabPanels>
  </Tabs>
  <component :is="addActivityComponent" />
</template>
<style scoped>
:deep(.p-tabview .p-tabview-panels) {
  padding-left: 0;
  padding-right: 0;
}

.p-button-secondary {
  background-color: lightgray;
  background: var(--white);
  border: 1px solid var(--black-16);
  color: var(--black-80);
}
</style>
